import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import {
    SettingsDocument,
    ThemeColorDefinitions,
    brandColor,
    normalizeSettingsDocument,
    themeRef,
    themeRefName,
} from '../api/models/configuration.model'
import { BrandService } from './brandService.service'
import { BrandTheme, ThemeMode, resolveBrandTheme } from './brand-theme.resolver'

/** How an element colour is currently set: pointing at the theme, or a colour of its own. */
export interface ColorSource {
    /** Theme colour it points at, or null when it carries its own value. */
    reference: string
    /** Value in use, whatever the source. */
    value: string
    /** True when the value comes from the definition rather than from something stored. */
    isDefault: boolean
    /**
     * True when this mode has a value of its own. False on a dark row means it is showing the
     * default, which is what tells the editor to label it instead of offering a reset that
     * would do nothing.
     */
    hasOwnValue: boolean
}

@Injectable({ providedIn: 'root' })
export class BrandEditorService {

    /** Theme currently applied to the page, so the editor can show the values in use. */
    readonly theme = new BehaviorSubject<BrandTheme>(null)

    constructor(private brandService: BrandService) { }

    async load(domain: string): Promise<void> {
        this.theme.next(await this.brandService.getBrandTheme(domain, true))
    }

    // ---- theme colours ----

    readonly themeColors = ThemeColorDefinitions

    themeValue(name: string, mode: ThemeMode): string {
        const scope = mode === 'dark' ? this.theme.value?.dark : this.theme.value?.light

        return scope?.theme[name] ?? ''
    }

    /** Empty when nothing was stored, which is what lets a dark field show as inherited. */
    storedThemeValue(name: string, mode: ThemeMode): string {
        return this.storedValue('theme', name, mode)
    }

    async setThemeColor(name: string, mode: ThemeMode, value: string): Promise<void> {
        if (!ThemeColorDefinitions.some(t => t.name === name)) return

        await this.write('theme', name, mode, value)
    }

    // ---- element colours ----

    /** Where an element colour comes from right now, for the inspector to show. */
    sourceOf(key: string, mode: ThemeMode): ColorSource {
        const definition = brandColor(key)
        const scope = mode === 'dark' ? this.theme.value?.dark : this.theme.value?.light
        const value = scope?.elements[key] ?? ''

        const own = this.storedValue('elements', key, mode)
        // Dark falls back to whatever light stores, so the source shown matches what is painted.
        const stored = own || (mode === 'dark' ? this.storedValue('elements', key, 'light') : '')

        if (!stored)
            return { reference: definition?.defaultRef ?? null, value, isDefault: true, hasOwnValue: false }

        return { reference: themeRefName(stored), value, isDefault: false, hasOwnValue: !!own }
    }

    /** Points an element at a theme colour. */
    async setReference(key: string, mode: ThemeMode, name: string): Promise<void> {
        await this.write('elements', key, mode, themeRef(name))
    }

    /** Gives an element a colour of its own, off the theme. */
    async setCustom(key: string, mode: ThemeMode, value: string): Promise<void> {
        await this.write('elements', key, mode, value)
    }

    /** Drops whatever was stored, so the element goes back to the theme colour it defaults to. */
    async reset(key: string, mode: ThemeMode): Promise<void> {
        await this.write('elements', key, mode, '')
    }

    // ---- the document as a whole ----

    /** The draft as stored, for callers that read sections this service has no opinion about. */
    get document(): SettingsDocument {
        return normalizeSettingsDocument(this.theme.value?.saved)
    }

    /**
     * Applies a change to the draft and saves it. Everything that edits a configuration goes
     * through here, so a colour and a piece of copy cannot overwrite each other.
     *
     * Repaints from the new values first and saves after, so dragging a picker feels immediate
     * instead of waiting on a round trip per step.
     */
    async save(mutate: (document: SettingsDocument) => void): Promise<void> {
        const saved = normalizeSettingsDocument(this.theme.value?.saved)

        mutate(saved)

        const resolved = resolveBrandTheme(saved)
        this.theme.next(resolved)
        this.brandService.applyTheme(resolved)

        await this.brandService.saveDraft(saved)
    }

    private storedValue(section: 'theme' | 'elements', key: string, mode: ThemeMode): string {
        return this.theme.value?.saved?.[section]?.[mode]?.[key] ?? ''
    }

    private async write(section: 'theme' | 'elements', key: string, mode: ThemeMode, value: string): Promise<void> {
        // Clearing removes the key rather than storing an empty string, so what is stored stays
        // a record of choices and everything else keeps following the theme.
        await this.save(document => {
            if (value)
                document[section][mode][key] = value
            else
                delete document[section][mode][key]
        })
    }
}
